import React, { useMemo, useState } from 'react';
import { Badge, Button, Input, Popover, Segmented, Select } from 'antd';
import { SearchOutlined, FilterOutlined, CloseOutlined } from '@ant-design/icons';
import { sourceIcon } from './liveChatMeta';

export const DEFAULT_FILTERS = {
  assigned: 'all',
  status: 'open',
  source_id: undefined,
  site: undefined,
  department_id: undefined,
  sub_department_id: undefined,
  search: '',
};

const chipClass = (selected) =>
  `inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs custom-font transition-colors cursor-pointer ${
    selected
      ? 'bg-brand-primary border-brand-primary text-white'
      : 'bg-white dark:bg-white/5 border-primary-100 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:border-brand-primary'
  }`;

const CountPill = ({ value, selected }) => (
  <span className={`rounded-full px-1.5 text-[10px] leading-4 ${selected ? 'bg-white/25' : 'bg-primary-50 dark:bg-white/10 text-brand-primary'}`}>
    {Number(value || 0).toLocaleString('fa-IR')}
  </span>
);

const Section = ({ title, children }) => (
  <div className="flex flex-col gap-2">
    <div className="text-xs font-bold text-gray-500 dark:text-gray-400 custom-font">{title}</div>
    <div className="flex flex-wrap gap-1.5">{children}</div>
  </div>
);

/**
 * فیلترهای صندوق گفتگو:
 *  - جستجو + دکمهٔ «فیلترها» (با شمارندهٔ فیلترهای فعال)
 *  - تب وضعیت و سوییچ ارجاع همیشه در دسترس
 *  - منبع/بخش/سایت داخل پنل بازشو؛ فیلترهای فعال به‌صورت چیپِ قابل‌حذف زیر جستجو
 */
const ConversationFilters = ({ filters, setFilters, me, sites, counts, departmentCounts, myOperatorId, total }) => {
  const [open, setOpen] = useState(false);
  const patch = (p) => setFilters((f) => ({ ...f, ...p }));

  const departments = useMemo(() => me.departments || [], [me.departments]);
  const sources = useMemo(() => me.sources || [], [me.sources]);
  // زیربخش‌ها فقط وقتی یک بخش مشخص انتخاب شده
  const subDepartments = useMemo(() => {
    if (!filters.department_id || filters.department_id === 'none') return [];
    return departments.find((d) => String(d.id) === String(filters.department_id))?.sub_departments || [];
  }, [departments, filters.department_id]);

  const activeChips = useMemo(() => {
    const chips = [];
    if (filters.source_id) {
      const s = sources.find((x) => x.id === filters.source_id);
      chips.push({ key: 'source_id', label: `منبع: ${s?.name ?? filters.source_id}` });
    }
    if (filters.department_id) {
      const d = departments.find((x) => String(x.id) === String(filters.department_id));
      chips.push({ key: 'department_id', label: `بخش: ${filters.department_id === 'none' ? 'بدون بخش' : d?.name ?? filters.department_id}` });
    }
    if (filters.sub_department_id) {
      const sub = subDepartments.find((x) => String(x.id) === String(filters.sub_department_id));
      chips.push({ key: 'sub_department_id', label: `زیربخش: ${filters.sub_department_id === 'none' ? 'بدون زیربخش' : sub?.name ?? filters.sub_department_id}` });
    }
    if (filters.site) chips.push({ key: 'site', label: `سایت: ${filters.site}`, ltr: true });
    return chips;
  }, [filters.source_id, filters.department_id, filters.sub_department_id, filters.site, sources, departments, subDepartments]);

  const dirtyCount = activeChips.length;
  const resetAll = () => setFilters({ ...DEFAULT_FILTERS, assigned: filters.assigned });

  const departmentChips = [
    { key: undefined, label: 'همه', count: Object.values(departmentCounts || {}).reduce((a, b) => a + b, 0) },
    ...departments.map((d) => ({ key: String(d.id), label: d.name, count: departmentCounts?.[String(d.id)] || 0 })),
    ...(departmentCounts?.none ? [{ key: 'none', label: 'بدون بخش', count: departmentCounts.none }] : []),
  ];

  const panel = (
    <div className="flex flex-col gap-4" style={{ width: 'min(320px, 80vw)' }}>
      <Section title="منبع ورودی">
        <button type="button" className={chipClass(!filters.source_id)} onClick={() => patch({ source_id: undefined })}>همه</button>
        {sources.map((s) => (
          <button key={s.id} type="button" className={chipClass(filters.source_id === s.id)} onClick={() => patch({ source_id: filters.source_id === s.id ? undefined : s.id })}>
            {sourceIcon(s)}
            {s.name}
          </button>
        ))}
      </Section>

      {departments.length > 0 && (
        <Section title="بخش">
          {departmentChips.map((chip) => {
            const selected = (filters.department_id ?? undefined) === chip.key;
            return (
              <button key={chip.key ?? 'all'} type="button" className={chipClass(selected)} onClick={() => patch({ department_id: chip.key, sub_department_id: undefined })}>
                {chip.label}
                <CountPill value={chip.count} selected={selected} />
              </button>
            );
          })}
        </Section>
      )}

      {subDepartments.length > 0 && (
        <Section title="زیربخش">
          {[{ key: undefined, label: 'همه' }, ...subDepartments.map((s) => ({ key: String(s.id), label: s.name })), { key: 'none', label: 'بدون زیربخش' }].map((chip) => (
            <button
              key={chip.key ?? 'all'}
              type="button"
              className={chipClass((filters.sub_department_id ?? undefined) === chip.key)}
              onClick={() => patch({ sub_department_id: chip.key })}
            >
              {chip.label}
            </button>
          ))}
        </Section>
      )}

      {sites.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-xs font-bold text-gray-500 dark:text-gray-400 custom-font">سایت مبدأ</div>
          <Select
            allowClear
            showSearch
            placeholder="همه سایت‌ها"
            value={filters.site}
            onChange={(v) => patch({ site: v })}
            className="custom-font w-full"
            getPopupContainer={(node) => node.parentElement}
            options={sites.map((s) => ({ value: s, label: <span dir="ltr">{s}</span> }))}
          />
        </div>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-white/10">
        <Button type="link" size="small" disabled={!dirtyCount} onClick={resetAll} className="custom-font !px-0">
          پاک کردن فیلترها
        </Button>
        <Button type="primary" size="small" onClick={() => setOpen(false)} className="custom-font">
          نمایش {Number(total || 0).toLocaleString('fa-IR')} گفتگو
        </Button>
      </div>
    </div>
  );

  const statusTabs = [
    { value: 'open', label: 'باز', count: counts.open },
    { value: 'closed', label: 'بسته' },
    { value: 'all', label: 'همه' },
    ...(me.is_supervisor ? [{ value: 'awaiting', label: 'در انتظار بخش', count: counts.awaiting }] : []),
  ];

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex gap-2">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-gray-400" />}
          placeholder="جستجو در نام، شماره یا متن"
          value={filters.search}
          onChange={(e) => patch({ search: e.target.value })}
          className="custom-font"
        />
        <Popover
          trigger="click"
          placement="bottomLeft"
          open={open}
          onOpenChange={setOpen}
          content={panel}
          arrow={false}
        >
          <Badge count={dirtyCount} size="small" offset={[-4, 4]} color="#0d887e">
            <Button icon={<FilterOutlined />} type={dirtyCount ? 'primary' : 'default'} ghost={Boolean(dirtyCount)} aria-label="فیلترها" />
          </Badge>
        </Popover>
      </div>

      <Segmented
        block
        value={filters.assigned}
        onChange={(v) => patch({ assigned: v })}
        options={[
          { value: 'all', label: <span className="custom-font">همه</span> },
          ...(myOperatorId ? [{ value: 'me', label: <span className="custom-font">من ({(counts.mine ?? 0).toLocaleString('fa-IR')})</span> }] : []),
          { value: 'unassigned', label: <span className="custom-font">بدون اپراتور ({(counts.unassigned ?? 0).toLocaleString('fa-IR')})</span> },
        ]}
      />

      <div className="flex items-center gap-4 border-b border-gray-100 dark:border-white/10 -mb-px overflow-x-auto" role="tablist" aria-label="وضعیت گفتگو">
        {statusTabs.map((t) => {
          const selected = filters.status === t.value;
          return (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => patch({ status: t.value })}
              className={`shrink-0 pb-2 pt-1 text-xs custom-font border-b-2 transition-colors cursor-pointer ${
                selected ? 'border-brand-primary text-brand-primary font-bold' : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              {t.label}
              {t.count ? <span className="ms-1 opacity-80">({Number(t.count).toLocaleString('fa-IR')})</span> : null}
            </button>
          );
        })}
      </div>

      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {activeChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 rounded-full bg-primary-50 dark:bg-white/10 text-brand-primary text-xs px-2.5 py-0.5 custom-font"
            >
              <span dir={chip.ltr ? 'ltr' : undefined}>{chip.label}</span>
              <button type="button" aria-label="حذف فیلتر" onClick={() => patch({ [chip.key]: undefined, ...(chip.key === 'department_id' ? { sub_department_id: undefined } : {}) })} className="inline-flex cursor-pointer opacity-70 hover:opacity-100">
                <CloseOutlined style={{ fontSize: 9 }} />
              </button>
            </span>
          ))}
          {activeChips.length > 1 && (
            <button type="button" onClick={resetAll} className="text-xs text-gray-500 hover:text-red-500 cursor-pointer custom-font">
              پاک کردن همه
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ConversationFilters;
